import React from 'react';
import cn from 'classnames';
import { iife, isSome, Maybe } from '@crocodille/util';

import styles from './input-envelope.module.scss';

export interface InputEnvelopeProps {
  /**
   * Label of the field.
   */
  readonly label: React.ReactNode;
  /**
   * Show the "required" indicator.
   */
  readonly required?: boolean;
  /**
   * Error to display.
   */
  readonly error?: Maybe<string>;
  /**
   * Class name for input envelope container.
   */
  readonly className?: string;
  /**
   * Class name for input container.
   */
  readonly innerClassName?: string;
  /**
   * Inline input envelope places label next to the input.
   *
   * * Use for checkboxes.
   */
  readonly inline?: boolean;
  /**
   * Does not use label element.
   *
   * * Use for selects.
   */
  readonly noLabelElement?: boolean;

  readonly isReverse?: boolean;
}

/**
 * Wraps an input element, provides error message and label.
 */
const InputEnvelope: React.FC<React.PropsWithChildren<InputEnvelopeProps>> = ({
  label,
  required = false,
  error,
  children,
  className,
  innerClassName,
  inline = false,
  noLabelElement = false,
  isReverse = false,
}) => {
  return (
    <div
      className={cn(
        styles.container,
        {
          [styles.isRequired]: required,
          [styles.hasError]: isSome(error),
          [styles.isInline]: inline,
          [styles.reverse]: isReverse,
        },
        className,
      )}
    >
      {iife(() => {
        const contents = (
          <>
            <span className={styles.label}>
              {label}
              <span
                className={cn(styles.requiredIndicator, {
                  [styles.isHidden]: !required,
                })}
                role="presentation"
                aria-hidden="true"
              >
                *
              </span>
            </span>
            {children}
          </>
        );

        if (noLabelElement) {
          return (
            <div className={cn(styles.containerInner, innerClassName)}>
              {children}
            </div>
          );
        } else {
          return (
            <label className={cn(styles.containerInner, innerClassName)}>
              {contents}
            </label>
          );
        }
      })}
      {isSome(error) ? (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default InputEnvelope;
